/**
 * Campo de uma premissa do simulador: valor editável, a fonte ao lado e um
 * link para voltar ao valor de mercado. Aceita vírgula ou ponto; valor fora da
 * faixa volta ao anterior em vez de virar NaN na conta.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Premissa } from './premissasMercado';

export const pct = (n: number, casas = 1): string =>
  Number.isFinite(n)
    ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`
    : '—';

const paraCampo = (v: number) => String(v).replace('.', ',');

type Props = {
  id: string;
  rotulo: string;
  premissa: Premissa;
  /** Texto depois do número: "%", "% a.a.". */
  sufixo?: string;
  maximo?: number;
};

export const CampoPremissa = ({ id, rotulo, premissa, sufixo = '%', maximo = 100 }: Props) => {
  const [texto, setTexto] = useState(paraCampo(premissa.valor));
  useEffect(() => {
    setTexto(paraCampo(premissa.valor));
  }, [premissa.valor]);

  const aplicar = () => {
    const n = Number(texto.trim().replace(',', '.'));
    if (texto.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= maximo) premissa.definir(n);
    else setTexto(paraCampo(premissa.valor));
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Label htmlFor={id}>{rotulo}</Label>
      <Input
        id={id}
        inputMode="decimal"
        className="h-8 w-20 text-right"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={aplicar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') aplicar();
        }}
      />
      <span>{sufixo}</span>
      <span className="text-xs text-muted-foreground">
        {premissa.editado ? 'Valor informado pelo vendedor' : premissa.mercado.fonte}
      </span>
      {premissa.editado && (
        <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={premissa.restaurar}>
          Usar {premissa.mercado.nome} ({pct(premissa.mercado.valor, 2)})
        </Button>
      )}
    </div>
  );
};
